import { isArray, isNull, mergeWith } from 'lodash';
import moment from 'moment';

import {
    CampaignCriteriaKey,
    getCampaignFilterCriteriaContainers,
    StatisticsByAllGoals
} from '../declarations/filter/filter';
import { flattenContainer } from '../service/FilterHelpers/FilterHelpers';
import { GridScreenLinkBuilder } from '../service/GridScreenLinkBuilder';
import { CampaignStrategyBudgetInfo, DayBudgetValue } from '../typings/budget';
import { campaigns, ClientInfo, groups } from '../typings/common';
import {
    Campaign,
    CampaignBaseInfo,
    CampaignPlatform,
    CampaignsCalcTypeFilter,
    CampaignStatusForFilter,
    CampaignStrategy,
    CampaignType,
    ClientData,
    StatusInfo,
    TimeTargetingSettings,
    TimeTargetingStatus
} from '../typings/domain';
import {
    CampaignFilterStrategyType,
    CampaignsQueryFilter,
    Criteria,
    EnumSetItem,
    FilterExpression,
    FilterExpressionEnumSetValue,
    FilterExpressionValue,
    FilterPartialExpression,
    GoalStatsQueryFilter,
    StatsQueryFilter
} from '../typings/filter';
import {
    campaignBaseInfoRowFragment,
    campaignFlatStrategyFragment,
    CampaignPayActionInfoQuery,
    CampaignsQuery_client_campaigns_rowset_GdCpmBannerCampaign,
    CampaignsQuery_client_campaigns_rowset_GdCpmBannerCampaign_brandSurveyStatus,
    campaignsRowFragment,
    campaignStatusInfoFragment,
    campaignStrategyBudgetFragment,
    CampaignTruncatedFragment,
    CurrencyCode,
    GdCampaignAction,
    GdCampaignBudgetPeriod,
    GdCampaignBudgetShowMode,
    GdCampaignStrategyType,
    GdCampaignStrategyTypeInput,
    GdiRecommendationType,
    TimeTargetStatus,
    timeTargetStatusInfoFragment
} from '../typings/graphql-grid';
import { GridSourceParams, PayActionInfo } from '../typings/grid';
import {
    RecommendationCharacter,
    RecommendationsIconType
} from '../typings/recommendations';

import {
    getValueFromApiFormat,
    mapBrandSurveyStatusRowToBrandSurveyStatus,
    mapCampaignRowTypeToCampaignType,
    mapStatsDataToStats
} from './common/common';
import {
    FilterCriteriaMappings,
    getStrictExpressions,
    textToCampaignFilterExpression
} from './filter/filter';
import { mapRowGoals } from './goal/goal';
import {
    isRecommendationSupported,
    mapContextRecommendationFromApiFormat
} from './recommendation';

export function mapRowToCampaignTruncatedBaseInfo(
    row: CampaignTruncatedFragment,
    client: ClientData,
    params: GridSourceParams
): CampaignBaseInfo {
    return {
        id: String(row.id),
        isAimingAllowed: row.isAimingAllowed,
        name: row.name,
        brandSurveyId: null,
        brandSurveyStatus: null,
        type: mapCampaignRowTypeToCampaignType(row.type),
        currency: row.currency === null ? CurrencyCode.RUB : row.currency,
        chiefLogin: client.chiefLogin,
        strategy: mapStrategy(row.flatStrategy),
        campaignUrl: GridScreenLinkBuilder.getContextTypeRelevantUrl(
            campaigns,
            groups,
            {
                login: params.login,
                contextIds: [String(row.id)]
            },
            params
        ),
        statusInfo: mapStatusToStatusInfo(row.status),
        access: row.access
    };
}

const getBrandLiftRow = (
    row: campaignBaseInfoRowFragment
): {
    brandSurveyId: string | null;
    brandSurveyStatus: CampaignsQuery_client_campaigns_rowset_GdCpmBannerCampaign_brandSurveyStatus | null;
} => {
    if (row.__typename === 'GdCpmBannerCampaign') {
        const data = row as CampaignsQuery_client_campaigns_rowset_GdCpmBannerCampaign;

        return {
            brandSurveyId: data.brandSurveyId,
            brandSurveyStatus: data.brandSurveyStatus
        };
    }

    return {
        brandSurveyId: null,
        brandSurveyStatus: null
    };
};

export function mapRowToCampaignBaseInfo(
    row: campaignBaseInfoRowFragment,
    client: ClientData,
    params: GridSourceParams
): CampaignBaseInfo {
    const { brandSurveyId, brandSurveyStatus } = getBrandLiftRow(row);

    return {
        id: String(row.id),
        isAimingAllowed: false,
        name: row.name,
        brandSurveyId,
        brandSurveyStatus: mapBrandSurveyStatusRowToBrandSurveyStatus(
            brandSurveyStatus
        ),
        type: mapCampaignRowTypeToCampaignType(row.type),
        currency: row.currency === null ? CurrencyCode.RUB : row.currency,
        chiefLogin: client.chiefLogin,
        strategy: mapStrategy(row.flatStrategy),
        campaignUrl: GridScreenLinkBuilder.getContextTypeRelevantUrl(
            campaigns,
            groups,
            {
                login: params.login,
                contextIds: [String(row.id)]
            },
            params
        ),
        statusInfo: mapStatusToStatusInfo(row.status),
        access: row.access
    };
}

function mapStrategy(strategy: campaignFlatStrategyFragment): CampaignStrategy {
    if (strategy.__typename === 'GdCampaignStrategyManual') {
        return {
            type: GdCampaignStrategyType.DEFAULT,
            isAutoBudget: strategy.isAutoBudget,
            platform: strategy.platform,
            separateBidding: getValueFromApiFormat(strategy.separateBidding)
        };
    } else if (strategy.type !== GdCampaignStrategyType.DEFAULT) {
        return {
            type: strategy.type,
            isAutoBudget: strategy.isAutoBudget,
            platform: strategy.platform
        };
    }
    throw Error('Неподдерживаемый тип стратегии');
}

export function mapRowToCampaign(
    row: campaignsRowFragment,
    client: ClientData,
    params: GridSourceParams
): Campaign {
    const recommendations = (row.recommendations || [])
        .filter(recommendation =>
            isRecommendationSupported(recommendation.type)
        )
        .map(mapContextRecommendationFromApiFormat);

    const rowGoalStats = row.goalStats || [];

    return Object.assign(mapRowToCampaignBaseInfo(row, client, params), {
        agencyId: row.agencyId ? String(row.agencyId) : null,
        name: row.name,
        currency: row.currency,
        type: mapCampaignRowTypeToCampaignType(row.type),
        stats: row.stats ? mapStatsDataToStats(row.stats) : {},
        goalStats: mapRowGoals(rowGoalStats),
        restSum: Number(row.sum),
        budget: getCampaignBudgetFromApiFormat(row.flatStrategy.budget),
        needPayment: row.status.needsNewPayment,
        moneyBlocked: row.status.moneyBlocked,
        hasBanners: row.hasAds,
        recommendations: {
            data: recommendations.length !== 0 ? recommendations : undefined,
            recommendationsIconType: recommendations.some(
                recommendation =>
                    recommendation.character ===
                    RecommendationCharacter.attention
            )
                ? RecommendationsIconType.red
                : RecommendationsIconType.yellow
        },
        startDate: moment(row.startDate),
        endDate: row.endDate ? moment(row.endDate) : undefined
    });
}

export function getCampaignBudgetFromApiFormat(
    budget: campaignStrategyBudgetFragment | null
): CampaignStrategyBudgetInfo {
    let value;

    if (budget) {
        switch (budget.period) {
            case GdCampaignBudgetPeriod.DAY:
                value = getDayBudgetValueFromApiFormat(
                    Number(budget.sum),
                    budget.showMode
                );
                break;
            case GdCampaignBudgetPeriod.WEEK:
                value = {
                    sum: Number(budget.sum)
                };
                break;
            case GdCampaignBudgetPeriod.CUSTOM:
                value = {
                    sum: Number(budget.sum),
                    start: budget.start,
                    finish: budget.finish
                };
                break;
            default:
                break;
        }
    }

    return {
        type: budget ? budget.period : null,
        value: value
    };
}

function getDayBudgetValueFromApiFormat(
    apiSumValue: number,
    apiModeValue: GdCampaignBudgetShowMode
): DayBudgetValue {
    return {
        isActive: apiSumValue !== 0,
        sum: apiSumValue,
        mode: isNull(apiModeValue)
            ? GdCampaignBudgetShowMode.DEFAULT
            : apiModeValue
    };
}

/**
 * Формирует данные с информацией о статусе кампании на основе данных от сервера
 */
export function mapStatusToStatusInfo(
    status: campaignStatusInfoFragment
): StatusInfo {
    const { primaryStatus, timeTargetStatus, primaryStatusDesc } = status;

    const result: StatusInfo = {
        type: primaryStatus,
        hintType: primaryStatusDesc === null ? undefined : primaryStatusDesc
    };

    result.timeTargetingSettings = getTimeTargetingSettingsFromApiFormat(
        timeTargetStatus
    );

    return result;
}

/**
 * Формирует данные о настройке таймтагретинга на основе данных от сервера
 */
export function getTimeTargetingSettingsFromApiFormat(
    timeTargetingStatusInfo: timeTargetStatusInfoFragment
): TimeTargetingSettings | undefined {
    const { status, activationTime } = timeTargetingStatusInfo;

    if (status === null || activationTime === null) {
        return;
    }

    const startTime = moment.parseZone(activationTime);

    return {
        startTime,
        status: getTimeTargetingStatusFromApiFormat(status)
    };
}

/**
 * Преобразует серверный статус таймтаргетинга в клиентский формат
 */
export function getTimeTargetingStatusFromApiFormat(
    apiStatus: TimeTargetStatus
): TimeTargetingStatus {
    const dictionary = {
        [TimeTargetStatus.ACTIVE]: TimeTargetingStatus.active,
        [TimeTargetStatus.TODAY]: TimeTargetingStatus.today,
        [TimeTargetStatus.TOMORROW]: TimeTargetingStatus.tomorrow,
        [TimeTargetStatus.THIS_WEEK]: TimeTargetingStatus.thisWeek,
        [TimeTargetStatus.LATER_THAN_WEEK]: TimeTargetingStatus.laterThanWeek
    };

    if (!dictionary[apiStatus]) {
        throw new Error(`Статус ${apiStatus} не поддерживается`);
    }

    return dictionary[apiStatus];
}

const mappings = new FilterCriteriaMappings<
    CampaignsQueryFilter & StatsQueryFilter & GoalStatsQueryFilter
>();
const { toBoolean, toDate, toNumber, toEnum } = mappings.valueMappings;

const campaignCriteriaMappings: {
    [key in CampaignCriteriaKey]: (
        expression: FilterExpression
    ) => CampaignsQueryFilter
} = {
    id: mappings.includes('campaignIdIn', 'campaignIdNotIn'),
    types: mappings.equality('typeIn', toEnum(CampaignType)),
    name: mappings.contains('nameContains', 'nameNotContains'),
    status: mappings.equality('statusIn', toEnum(CampaignStatusForFilter)),
    // hasDailyBudgetLimit: mappings.boolean('hasDailyBudget', toBoolean),
    startDate: mappings.comparison(
        'minStartDate',
        'maxStartDate',
        toDate,
        false
    ),
    endDate: mappings.comparison(
        'minFinishDate',
        'maxFinishDate',
        toDate,
        false
    ),
    platform: mappings.equality('platformIn', toEnum(CampaignPlatform)),
    recommendations: mappings.equality(
        'recommendations',
        toEnum(GdiRecommendationType)
    ),
    strategy: mappings.equality(
        'strategyTypeIn',
        toEnum(GdCampaignStrategyTypeInput)
    ),
    hasBidModifiers: mappings.boolean('hasBidModifiers', toBoolean),
    hasBroadmatch: mappings.boolean('hasBroadmatch', toBoolean),
    hasMetrika: mappings.boolean('hasMetrika', toBoolean),
    hasSiteMonitoring: mappings.boolean('hasSiteMonitoring', toBoolean),
    cost: mappings.comparison('minCost', 'maxCost', toNumber),
    revenue: mappings.comparison('minRevenue', 'maxRevenue', toNumber),
    profitability: mappings.comparison(
        'minProfitability',
        'maxProfitability',
        toNumber
    ),
    avgGoalCost: (expression: FilterExpression): CampaignsQueryFilter => {
        if (
            expression.additionalCriteriaKey &&
            expression.additionalCriteriaKey !== StatisticsByAllGoals
        ) {
            return mappings.comparison(
                'minCostPerAction',
                'maxCostPerAction',
                toNumber,
                false,
                expression.additionalCriteriaKey
            )(expression);
        }

        return mappings.comparison(
            'minAvgGoalCost',
            'maxAvgGoalCost',
            toNumber
        )(expression);
    },
    avgClickCost: mappings.comparison(
        'minAvgClickCost',
        'maxAvgClickCost',
        toNumber
    ),
    avgCpmCost: mappings.comparison('minCpmPrice', 'maxCpmPrice', toNumber),
    shows: mappings.comparison('minShows', 'maxShows', toNumber),
    clicks: mappings.comparison('minClicks', 'maxClicks', toNumber),
    ctr: mappings.comparison('minCtr', 'maxCtr', toNumber),
    goals: (expression: FilterExpression): CampaignsQueryFilter => {
        if (
            expression.additionalCriteriaKey &&
            expression.additionalCriteriaKey !== StatisticsByAllGoals
        ) {
            return mappings.comparison(
                'minGoals',
                'maxGoals',
                toNumber,
                false,
                expression.additionalCriteriaKey
            )(expression);
        }

        return mappings.comparison('minGoals', 'maxGoals', toNumber)(
            expression
        );
    },
    conversionRate: (expression: FilterExpression): CampaignsQueryFilter => {
        if (
            expression.additionalCriteriaKey &&
            expression.additionalCriteriaKey !== StatisticsByAllGoals
        ) {
            return mappings.comparison(
                'minConversionRate',
                'maxConversionRate',
                toNumber,
                false,
                expression.additionalCriteriaKey
            )(expression);
        }

        return mappings.comparison(
            'minConversionRate',
            'maxConversionRate',
            toNumber
        )(expression);
    },
    bounceRate: mappings.comparison('minBounceRate', 'maxBounceRate', toNumber)
};

function appendSameNameToStrategiesList(
    v: FilterExpressionValue
): FilterExpressionValue {
    const enumSet = v.value as FilterExpressionEnumSetValue;

    [
        {
            name: CampaignFilterStrategyType.DEFAULT,
            doubleName: GdCampaignStrategyType.CPM_DEFAULT
        },
        {
            name: CampaignFilterStrategyType.CPM_MAX_REACH,
            doubleName: GdCampaignStrategyType.CPM_MAX_REACH_CUSTOM_PERIOD
        },
        {
            name: CampaignFilterStrategyType.CPM_MAX_IMPRESSIONS,
            doubleName: GdCampaignStrategyType.CPM_MAX_IMPRESSIONS_CUSTOM_PERIOD
        }
    ].forEach(pair => {
        if (
            enumSet.find((value: EnumSetItem) => {
                return value.key === pair.name;
            })
        ) {
            enumSet.push({ key: pair.doubleName, title: '' });
        }
    });

    return v;
}

const expressionToCampaignsQueryFilter = (
    expression: FilterExpression
): CampaignsQueryFilter => {
    // дописываем к ручным стратегиям (DEFAULT)
    // ручные стратегии для медийки (CPM_DEFAULT)
    if (expression.criteriaKey === CampaignCriteriaKey.strategy) {
        expression.value = appendSameNameToStrategiesList(expression.value);
    }

    return campaignCriteriaMappings[expression.criteriaKey](expression);
};

export const mapCampaignFilterToApiFormat = (
    expressions: FilterPartialExpression[],
    clientInfo: ClientInfo,
    isBidCorrectionSearchEnabled?: boolean,
    calcType?: CampaignsCalcTypeFilter
): CampaignsQueryFilter => {
    const criteria: Criteria[] = [];

    getCampaignFilterCriteriaContainers(
        clientInfo,
        isBidCorrectionSearchEnabled,
        calcType === CampaignsCalcTypeFilter.CPM
    ).forEach(({ criteria: c }) => criteria.push(...flattenContainer(c)));

    return getStrictExpressions(
        expressions,
        criteria,
        textToCampaignFilterExpression
    )
        .map(expressionToCampaignsQueryFilter)
        .reduce(
            (obj, otherObj) =>
                mergeWith(obj, otherObj, (objValue, srcValue) => {
                    if (isArray(objValue)) {
                        return objValue.concat(srcValue);
                    }

                    return undefined;
                }),
            {}
        );
};

export const isCampaignEditable = (campaign: CampaignBaseInfo): boolean => {
    return campaign.access.canEdit;
};

export function mapCampaignTypesQueryResponseToCampaignTypes(
    data: CampaignPayActionInfoQuery
): PayActionInfo {
    return {
        campaignTypes: data.client.campaigns.features.campaignTypes,
        unsupportedCampaigns: data.client.campaigns.rowset
            .filter(
                el => !el.access.actions.includes(GdCampaignAction.PAY_BY_CASH)
            )
            .map(el => ({
                id: String(el.id),
                name: el.name
            }))
    };
}
